/**
 * coveragePayment — 補償（NOC / CDW）を Square で払ったかの判定結果を、サイドパネル用の表示に変える（2026-10-31）
 *
 * 判定そのものはサーバー側 sys/api/payment/coverage_payment.php の1か所だけ（事前受付フォームと共通）。
 * search_by_contact.php に invoice_id を渡すと、レスポンスの coverage_payment に入って返る。
 * ここでは判定し直さない。知らない値・欠けた値は「判定できない」にする（誤って「支払い済み」を出さない）。
 */

export type CoveragePaymentStatus = 'paid' | 'unpaid' | 'unknown';

export interface CoveragePaymentResult {
  noc: CoveragePaymentStatus;
  cdw: CoveragePaymentStatus;
  reason: string;
}

export function normalizeCoverageStatus(v: unknown): CoveragePaymentStatus {
  return v === 'paid' || v === 'unpaid' ? v : 'unknown';
}

/** search_by_contact のレスポンスから取り出す。無ければ null（invoice_id を渡していない等） */
export function readCoveragePayment(resp: unknown): CoveragePaymentResult | null {
  const raw = (resp as { coverage_payment?: unknown } | null)?.coverage_payment;
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  return {
    noc: normalizeCoverageStatus(r.noc),
    cdw: normalizeCoverageStatus(r.cdw),
    reason: typeof r.reason === 'string' ? r.reason : '',
  };
}

/** 判定できなかった理由（スタッフ向けの補足） */
const REASON_LABELS: Record<string, string> = {
  no_invoice: '請求書なし',
  bad_invoice_link: '請求書リンクが不正',
  no_token: 'Square の設定なし',
  lookup_failed: 'Square に問い合わせできず',
  no_order: '明細を取得できず',
  refunded: '返金あり',
  multi_vehicle: '複数台の請求書',
  invoice_partially_paid: '一部だけ支払い',
  invoice_partially_refunded: '一部返金',
  invoice_refunded: '返金済み',
  invoice_canceled: '請求書取消',
  invoice_failed: '請求書失敗',
  invoice_payment_pending: '支払い処理中',
};

export function coveragePaymentLabel(
  status: CoveragePaymentStatus,
  reason = '',
): { text: string; color: string; bg: string } {
  if (status === 'paid') return { text: '支払い済み', color: '#166534', bg: '#dcfce7' };
  if (status === 'unpaid') return { text: '未払い', color: '#9a3412', bg: '#ffedd5' };
  const why = REASON_LABELS[reason];
  return { text: why ? `判定できない（${why}）` : '判定できない', color: '#475569', bg: '#f1f5f9' };
}
